import React from 'react';
import { motion } from 'framer-motion';
import { Briefcase } from 'lucide-react';

interface RecruiterLoadingScreenProps {
  icon?: React.ComponentType<{ className?: string }>;
}

/**
 * Full-page recruiter loader — frosted card + gliding progress bar.
 * Icon-only; no per-page copy (consistent across recruiter surfaces).
 */
export function RecruiterLoadingScreen({ icon: Icon = Briefcase }: RecruiterLoadingScreenProps) {
  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-slate-50 px-4">
      <motion.div
        initial={{ opacity: 0, y: 10, scale: 0.985 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        className="relative flex w-full max-w-sm flex-col items-center rounded-[28px] border border-white/70 bg-white/70 px-10 py-12 text-center shadow-[0_12px_50px_-12px_rgba(15,23,42,0.18)] backdrop-blur-xl"
        role="status"
        aria-label="Loading"
        aria-live="polite"
        aria-busy="true"
      >
        <div className="relative mb-7 flex items-center justify-center">
          <motion.span
            aria-hidden
            className="absolute inset-0 rounded-2xl bg-pink-500/20"
            animate={{ scale: [1, 1.35, 1], opacity: [0.45, 0, 0.45] }}
            transition={{ duration: 2.2, ease: 'easeInOut', repeat: Infinity }}
          />
          <span className="relative flex size-14 items-center justify-center rounded-2xl bg-gradient-to-br from-pink-500 to-rose-500 shadow-lg shadow-pink-500/25">
            <Icon className="size-6 text-white" />
          </span>
        </div>

        <div className="relative h-1 w-40 overflow-hidden rounded-full bg-slate-200/80">
          <motion.span
            aria-hidden
            className="absolute inset-y-0 left-0 w-1/2 rounded-full bg-gradient-to-r from-pink-500 to-rose-500"
            animate={{ x: ['-110%', '220%'] }}
            transition={{ duration: 1.5, ease: 'easeInOut', repeat: Infinity }}
          />
        </div>
      </motion.div>
    </div>
  );
}
